import { Container, Graphics } from '../../../vendor/pixi.min.mjs';

/**
 * Wind-driven falling leaves over the train sim board.
 *
 * Cosmetic-only continuous-emitter particle system, in the same spirit
 * as `spawnGoalConfetti` / `tickGoalConfetti` on `TrainSimBoard` — just
 * with very different physics:
 *
 *   • Each leaf drifts RIGHTWARD (matching cloud direction so the whole
 *     map shares one coherent "wind" cue) with a small downward fall.
 *   • A sine wobble on `vy` makes each leaf flutter as it falls instead
 *     of dropping in a straight line — that single tweak is most of
 *     what reads as "leaf in the wind" rather than "rectangle fading".
 *   • Each leaf tumbles (rotates) at its own random angular speed.
 *   • Particles spawn just outside the LEFT edge (and a fraction at
 *     the TOP) so the screen always has fresh leaves entering, and
 *     despawn once they exit the RIGHT or BOTTOM edge.
 *   • A soft cap (LEAF_CAP) bounds the per-frame particle count so
 *     long sessions can't accumulate Graphics objects.
 *
 * Sits in its OWN Container which the board parents at a high zIndex
 * so the leaves visibly drift over tracks, decorations and trains —
 * the way real wind looks atmospheric in front of the world. Alpha is
 * <1 and sizes are small (~6–12 px) so readability of switches /
 * lamps / signals never suffers.
 *
 * Pure procedural Graphics — no theme art, no asset paths. Skipping
 * the SkinResolver keeps the effect cheap and means the user can't
 * accidentally break it by clearing some slot.
 */

/**
 * Spring/summer palette — every leaf is a different shade of green so
 * the canopy reads as fresh foliage tumbling on the breeze rather than
 * an autumn shed. Range spans pale lime → deep forest with a couple
 * of yellow-greens at the top end so the lighter leaves catch the
 * eye against bright skies and the darker ones still register over
 * grass tiles.
 */
const LEAF_COLORS = [
    0xd9f99d, // pale lime
    0xbef264, // light lime
    0xa3e635, // lime
    0x84cc16, // fresh green
    0x65a30d, // grass green
    0x4d7c0f, // olive green
    0x16a34a, // emerald
    0x15803d, // forest green
    0x166534, // deep forest
];

/**
 * Average particle spawns per Pixi-ticker frame. 0.18 ≈ ~11/s at 60 fps —
 * enough to keep the air populated without flooding the canvas. Capped
 * by LEAF_CAP, so once the air is "full" the spawn loop just no-ops
 * extra ticks until existing leaves drift off the edge.
 */
const SPAWN_PER_FRAME = 0.18;

/** Soft cap on simultaneous leaves. 50 stays cheap on mobile GPUs. */
const LEAF_CAP = 50;

/** Per-leaf horizontal drift (px / Pixi-ticker-frame). Same direction
 *  as `tickClouds` so the wind cue is coherent across the whole sky. */
const VX_MIN = 0.7;
const VX_MAX = 1.6;

/** Base downward fall speed before the wobble adds its sine swing. */
const VY_MIN = 0.10;
const VY_MAX = 0.35;

/** Wobble amplitude and frequency on the y-axis — the "flutter" beat. */
const WOB_AMP_MIN = 0.25;
const WOB_AMP_MAX = 0.75;
const WOB_FREQ_MIN = 1.5;
const WOB_FREQ_MAX = 3.2;

/** Tumble (rotation) speed in rad / Pixi-ticker-frame. */
const VROT_MIN = -0.07;
const VROT_MAX =  0.07;

/** Render size, in px (height of the leaf "lemon" shape). */
const SIZE_MIN = 6;
const SIZE_MAX = 12;

/** Fraction of new spawns that enter from the TOP edge instead of the
 *  LEFT edge. Mixing a few top-spawns fills the screen faster on stage
 *  load and breaks the "single horizontal stream" look. */
const TOP_SPAWN_FRACTION = 0.30;

/** Off-board buffer (px) — leaves spawn / despawn just outside the
 *  visible bounds so the user never sees one pop in or out. */
const EDGE_BUFFER = 32;

export class TrainSimLeaves {
    /** @type {Container} */
    container = new Container();

    /**
     * Live particles. Each entry stores the Graphics handle plus the
     * physics state we update each frame — kept as a flat object so the
     * tick loop is a tight property-access pass with no allocation.
     *
     * @type {Array<{
     *   gfx: Graphics,
     *   vx: number, vy: number,
     *   wobAmp: number, wobFreq: number, wobPhase: number,
     *   vrot: number,
     *   t: number,
     * }>}
     */
    _leaves = [];

    /** Drift box in board-local coords: { x, y, w, h }. */
    _bounds = { x: 0, y: 0, w: 0, h: 0 };

    /** Fractional spawn accumulator — we emit floor(_spawnAccum) leaves
     *  per frame and keep the remainder so the average spawn rate stays
     *  exact across variable frame deltas. */
    _spawnAccum = 0;

    /**
     * @param {{ x: number, y: number, w: number, h: number }} bounds
     *        World rect the leaves are allowed to drift through. The
     *        train sim board uses local coords, so x/y are typically 0
     *        and w/h are `cols * cellSize` / `rows * cellSize`.
     */
    constructor(bounds) {
        this.container.eventMode = 'none'; // never intercept input
        this.container.label = 'wind-leaves';
        this.setBounds(bounds);
    }

    setBounds(b) {
        this._bounds = {
            x: b?.x || 0,
            y: b?.y || 0,
            w: Math.max(1, b?.w || 1),
            h: Math.max(1, b?.h || 1),
        };
    }

    /**
     * Per-frame update. `delta` is in Pixi ticker units (1 ≈ 16.67 ms
     * at 60 fps), matching the rest of the trainsim tick suite.
     *
     *   1. Accumulate emission credits and spawn new leaves up to the
     *      soft cap.
     *   2. Move every live leaf: horizontal drift + (gravity + sine
     *      wobble) on y + tumble.
     *   3. Despawn anything past the right or bottom edge.
     *
     * Real-time tick (no speed-multiplier) — the wind shouldn't
     * change tempo when the player presses fast-forward.
     */
    update(delta) {
        const step = delta || 1;
        const dtSec = step / 60;

        // ── 1. Emission ───────────────────────────────────────────
        this._spawnAccum += SPAWN_PER_FRAME * step;
        // Clamp the carry-over so a long pause (tab backgrounded) doesn't
        // blast a hundred leaves the moment the tab regains focus.
        if (this._spawnAccum > 5) this._spawnAccum = 5;
        while (this._spawnAccum >= 1) {
            this._spawnAccum -= 1;
            if (this._leaves.length >= LEAF_CAP) break;
            this._spawnLeaf();
        }

        // ── 2. + 3. Integrate + despawn ───────────────────────────
        const rightEdge  = this._bounds.x + this._bounds.w + EDGE_BUFFER;
        const bottomEdge = this._bounds.y + this._bounds.h + EDGE_BUFFER;
        for (let i = this._leaves.length - 1; i >= 0; i--) {
            const l = this._leaves[i];
            const g = l.gfx;
            if (!g || g.destroyed) {
                this._leaves.splice(i, 1);
                continue;
            }
            l.t += dtSec;
            const wob = Math.sin(l.t * l.wobFreq + l.wobPhase) * l.wobAmp;
            g.x += l.vx * step;
            g.y += (l.vy + wob) * step;
            g.rotation += l.vrot * step;
            if (g.x > rightEdge || g.y > bottomEdge) {
                if (g.parent) g.parent.removeChild(g);
                g.destroy?.();
                this._leaves.splice(i, 1);
            }
        }
    }

    /**
     * Spawn one leaf with randomised colour, size, velocity, wobble
     * and tumble. Spawn position is mostly on the LEFT edge (so the
     * wind cue reads "moving rightward across the world") with a
     * fraction on the TOP edge for visual variety.
     */
    _spawnLeaf() {
        const color = LEAF_COLORS[(Math.random() * LEAF_COLORS.length) | 0];
        const h = SIZE_MIN + Math.random() * (SIZE_MAX - SIZE_MIN);
        const w = h * 0.55; // leaf is ~half as wide as it is tall

        // Pointed-oval ("lemon") silhouette — two opposing quadratic
        // arcs. Cheaper than a 4-segment bezier teardrop, and at this
        // size the eye reads the result as a tumbling leaf either way.
        const g = new Graphics();
        g.moveTo(0, -h / 2);
        g.quadraticCurveTo(w, 0, 0, h / 2);
        g.quadraticCurveTo(-w, 0, 0, -h / 2);
        g.fill({ color, alpha: 0.92 });
        g.stroke({ color: 0x0b1220, width: 0.5, alpha: 0.4 });
        // Single vein down the middle so the silhouette doesn't read
        // as a featureless blob when it tumbles.
        g.moveTo(0, -h / 2);
        g.lineTo(0, h / 2);
        g.stroke({ color: 0x0b1220, width: 0.4, alpha: 0.35 });

        const fromTop = Math.random() < TOP_SPAWN_FRACTION;
        if (fromTop) {
            // Top edge: pick an X in the LEFT 75 % of the board so the
            // leaf has room to drift rightward before exiting.
            g.x = this._bounds.x + Math.random() * this._bounds.w * 0.75;
            g.y = this._bounds.y - EDGE_BUFFER;
        } else {
            g.x = this._bounds.x - EDGE_BUFFER;
            g.y = this._bounds.y + Math.random() * this._bounds.h;
        }
        g.rotation = Math.random() * Math.PI * 2;
        this.container.addChild(g);

        this._leaves.push({
            gfx: g,
            vx:       VX_MIN  + Math.random() * (VX_MAX  - VX_MIN),
            vy:       VY_MIN  + Math.random() * (VY_MAX  - VY_MIN),
            wobAmp:   WOB_AMP_MIN  + Math.random() * (WOB_AMP_MAX  - WOB_AMP_MIN),
            wobFreq:  WOB_FREQ_MIN + Math.random() * (WOB_FREQ_MAX - WOB_FREQ_MIN),
            wobPhase: Math.random() * Math.PI * 2,
            vrot:     VROT_MIN + Math.random() * (VROT_MAX - VROT_MIN),
            t: 0,
        });
    }

    destroy() {
        for (const l of this._leaves) {
            if (l.gfx && !l.gfx.destroyed) l.gfx.destroy?.();
        }
        this._leaves.length = 0;
        if (this.container && !this.container.destroyed) {
            if (this.container.parent) this.container.parent.removeChild(this.container);
            this.container.destroy({ children: true });
        }
    }
}
